import { ApolloProvider } from '@apollo/client';
import { SmallScreenWarningPopup } from '@modules/SmallScreenWarningPopup';
import { configureAndInitializeAmplify } from '@modules/authentication/utils/configureAndInitializeAmplify';
import { MuiModalProvider } from '@modules/common/components/Modal/MuiModalProvider';
import { NotificationProvider } from '@modules/common/components/Notification/NotificationProvider';
import {
  PROXY_HOST_URL,
  RETURN_PATH_QUERY_PARAM,
} from '@modules/common/constants';
import { Middleware } from '@modules/services/Middleware';
import { ThemeProvider } from '@modules/theme/ThemeProvider';
import { ThemeModeHandler } from '@modules/theme/components/ThemeModeHandler';
import GlobalStyles from '@mui/material/GlobalStyles';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { captureException } from '@sentry/react';
import { Auth } from 'aws-amplify';
import { getPersistor } from 'getPersistor';
import { Suspense, useCallback, useEffect, useState } from 'react';
import { hot } from 'react-hot-loader/root';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from 'react-router-dom';
import { IntercomProvider } from 'react-use-intercom';
import EmptyRoute from 'router/RouterComponent/EmptyRoute';
import PrivateRoute from 'router/RouterComponent/PrivateRoute';
import ScrollToTop from 'router/ScrollToTop';
import AuthService from 'services/AuthService';
import { AuthProvider } from 'v2/core/clients/authAmplify';
import { FeatureFlagProvider } from 'v2/core/clients/featureFlag';
import lazyLoadWithRetry from 'v2/core/modules/lazyLoadWithRetry';
import UserContextProvider from 'v2/core/services/user';
import { errorToast } from 'v2/core/utils/toast';
import FullscreenLoader from 'v2/view/components/FullscreenLoader/FullscreenLoader';
import Toasts from 'v2/view/components/Toasts/Toasts';
import ErrorBoundary from 'v2/views/components/error/boundary';
import { getApolloClient } from './ApolloClient';
import './App.scss';

const ForgotPasswordContainer = lazyLoadWithRetry(() =>
  import(
    '@modules/authentication/components/ForgotPasswordContainer' /* webpackChunkName: "ForgotPasswordContainer" */
  ),
);

const AuthenticationModule = lazyLoadWithRetry(() =>
  import(
    'v3/modules/authentication/components/AuthenticationModule' /* webpackChunkName: "Integrations" */
  ),
);

const HealthModule = lazyLoadWithRetry(() =>
  import('v3/modules/health' /* webpackChunkName: "HealthModule" */),
);

const Index = lazyLoadWithRetry(() =>
  import('modules/ui' /* webpackChunkName: "IndexModule" */),
);

const loadSegment = () => {
  let analytics = window?.analytics || [];
  window.analytics = analytics;

  if (analytics.initialize) {
    return analytics;
  }

  if (analytics.invoked) {
    return analytics;
  }

  window.analytics.invoked = !0;

  analytics.methods = [
    'trackSubmit',
    'trackClick',
    'trackLink',
    'trackForm',
    'pageview',
    'identify',
    'reset',
    'group',
    'track',
    'ready',
    'alias',
    'debug',
    'page',
    'once',
    'off',
    'on',
    'addSourceMiddleware',
    'addIntegrationMiddleware',
    'setAnonymousId',
    'addDestinationMiddleware',
  ];

  analytics.factory = function (method) {
    return function () {
      var args = Array.prototype.slice.call(arguments);
      args.unshift(method);
      analytics.push(args);
      return analytics;
    };
  };

  for (var i = 0; i < analytics.methods.length; i++) {
    var key = analytics.methods[i];
    analytics[key] = analytics.factory(key);
  }

  analytics._writeKey = '4lXwEA9clqezS6spw3wEbAHgf9FyBjgU';
  analytics.SNIPPET_VERSION = '4.15.3';
  AnalyticsBrowser.load(
    {
      writeKey: '4lXwEA9clqezS6spw3wEbAHgf9FyBjgU',
    },
    {
      integrations: {
        'Segment.io': {
          apiHost: `${PROXY_HOST_URL}/api.segment.io/v1`,
          protocol: 'https',
        },
      },
    },
  )
    .then(([response]) => {
      analytics = response;
      window.analytics = response;
    })
    .catch(() => {});
};

export const getSearchParamsForRedirectRoute = () => {
  const pathName = window.location.pathname;

  if (pathName === '/logout') {
    return '';
  }

  const hasValidReturnToPath = pathName && pathName !== '/';

  const searchParams = new URLSearchParams(window.location.search);
  const allQueryParamString = searchParams.toString();

  return hasValidReturnToPath
    ? `?${RETURN_PATH_QUERY_PARAM}=${pathName}${
        allQueryParamString ? `&${allQueryParamString.replace('?', '')}` : ''
      }`
    : allQueryParamString;
};

const App = () => {
  const [basename, setBasename] = useState();
  const allSearchParams = new URLSearchParams(window.location.search);

  const { error: errorInUrl } = Object.fromEntries(allSearchParams);

  const apolloClient = getApolloClient(true);
  const [persistor, setPersistor] = useState();

  useEffect(() => {
    async function init() {
      const newPersistor = getPersistor(true);
      await newPersistor.restore();
      setPersistor(newPersistor);
    }

    init().catch(captureException);

    loadSegment();
  }, []);

  const clearCache = useCallback(async () => {
    if (!persistor) {
      return;
    }

    await persistor.purge();
  }, [persistor]);

  const logoutAction = async () => {
    try {
      await clearCache();

      // Resolving "Store reset while query was in flight (not completed in
      // link chain". Call this method to terminate any active client
      // processes, making it safe to dispose of this ApolloClient instance.
      // I wasn't able to reproduce this issue, but it's possible that it
      // could happen again.

      // For more information, check out https://www.apollographql.com/docs/react/api/core/ApolloClient/#ApolloClient.stop.
      // Related GitHub issues:
      // 1. https://github.com/apollographql/apollo-client/issues/3766
      // 2. https://github.com/apollographql/apollo-client/issues/2919
      apolloClient.stop();

      await apolloClient.clearStore();
    } catch (e) {
      captureException(e);
    } finally {
      Auth.signOut().then(() => (window.location.href = '/'));
    }
  };

  useEffect(() => {
    if (errorInUrl) {
      errorToast('Something went wrong');
      console.error(decodeURIComponent(`${errorInUrl}`));
    }
  }, [errorInUrl]);

  const searchParams = getSearchParamsForRedirectRoute();

  if (AuthService.isGoogleAuth()) {
    configureAndInitializeAmplify();
  }

  return (
    <ThemeProvider>
      <GlobalStyles
        styles={({ palette }) => ({
          body: {
            backgroundColor: palette.background.default,
            color: palette.text.primary,
          },
          a: {
            color: palette.primary.main,
            '&:hover': {
              color: palette.primary.main,
              textDecoration: 'underline',
            },
          },
        })}
      />
      <ApolloProvider client={apolloClient}>
        <AuthProvider>
          <FeatureFlagProvider>
            <ErrorBoundary>
              <Router basename={basename}>
                <UserContextProvider>
                  <IntercomProvider
                    appId={process.env.REACT_APP_INTERCOM_APP_ID}
                  >
                    <ThemeModeHandler />
                    <Middleware />
                    <NotificationProvider>
                      <MuiModalProvider>
                        <Suspense fallback={<FullscreenLoader />}>
                          <SmallScreenWarningPopup />
                          <Toasts />
                          <FullscreenLoader />
                          <ScrollToTop>
                            <Routes>
                              <Route
                                path="/login"
                                element={
                                  <EmptyRoute
                                    component={AuthenticationModule}
                                    title="Login"
                                  />
                                }
                              />
                              <Route
                                path="/login/:type"
                                element={
                                  <EmptyRoute
                                    component={AuthenticationModule}
                                    title="Login"
                                  />
                                }
                              />
                              <Route
                                path="/login/saml/:type"
                                element={
                                  <EmptyRoute
                                    component={AuthenticationModule}
                                    title="Login"
                                  />
                                }
                              />
                              <Route
                                path="/forgot-password"
                                element={
                                  <EmptyRoute
                                    component={ForgotPasswordContainer}
                                    title="Forgot password"
                                  />
                                }
                              />
                              <Route
                                path="/health"
                                element={
                                  <EmptyRoute
                                    forBothAuthStates={true}
                                    component={HealthModule}
                                    title="Health check"
                                  />
                                }
                              />
                              {AuthService.isAuthenticated() ? (
                                <Route
                                  path="/*"
                                  element={
                                    <PrivateRoute
                                      basename={basename}
                                      setBasename={setBasename}
                                      logoutAction={logoutAction}
                                      title="Root"
                                      component={Index}
                                    />
                                  }
                                />
                              ) : (
                                <Route
                                  path="/*"
                                  element={
                                    <Navigate
                                      replace
                                      to={{
                                        pathname: '/login',
                                        search: searchParams,
                                      }}
                                    />
                                  }
                                />
                              )}
                              <Route
                                path="*"
                                element={<Navigate replace to="/" />}
                              />
                            </Routes>
                          </ScrollToTop>
                        </Suspense>
                      </MuiModalProvider>
                    </NotificationProvider>
                  </IntercomProvider>
                </UserContextProvider>
              </Router>
            </ErrorBoundary>
          </FeatureFlagProvider>
        </AuthProvider>
      </ApolloProvider>
    </ThemeProvider>
  );
};

export default hot(App);
